import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchPublicPostPreview,
  fetchPublicProfilePreview,
  type PublicPostPreview,
  type PublicProfilePreview,
} from "@/api/publicPreview";
import { Button } from "@/components/ui/Button";
import {
  buildAndroidIntentUrl,
  buildPlayStoreUrl,
  type AppRouteKind,
} from "@/lib/appLinks";
import { isAndroidDevice } from "@/lib/invite";
import { useNoIndex } from "@/lib/noIndex";
import { tw } from "@/lib/theme";

const COPY: Record<AppRouteKind, { title: string; description: string }> = {
  post: {
    title: "View this Frame in Daywia",
    description: "Open the Android app to see the full post and conversation.",
  },
  user: {
    title: "View this Daywia profile",
    description: "Open the Android app to follow, connect, and see their Frames.",
  },
  parallel: {
    title: "Open this Parallel",
    description: "Parallel details and participation are available in the Android app.",
  },
  chat: {
    title: "Open your Daywia chat",
    description: "Chats are private. Sign in in the Android app to continue securely.",
  },
  notifications: {
    title: "Open your notifications",
    description: "Notifications are private. Sign in in the Android app to view them.",
  },
};

interface AppLinkLandingPageProps {
  kind: AppRouteKind;
}

export function AppLinkLandingPage({ kind }: AppLinkLandingPageProps) {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<PublicProfilePreview | null>(null);
  const [post, setPost] = useState<PublicPostPreview | null>(null);
  const copy = COPY[kind];
  const isPrivateRoute = kind === "chat" || kind === "notifications";
  useNoIndex(isPrivateRoute);

  useEffect(() => {
    document.title = `${copy.title} | Daywia`;
  }, [copy.title]);

  useEffect(() => {
    setProfile(null);
    setPost(null);
    if (!id) return;
    if (kind === "user") {
      fetchPublicProfilePreview(id).then(setProfile).catch(() => setProfile(null));
    } else if (kind === "post") {
      fetchPublicPostPreview(id).then(setPost).catch(() => setPost(null));
    }
  }, [id, kind]);

  function openApp() {
    if (isAndroidDevice()) {
      window.location.href = buildAndroidIntentUrl(kind, id);
      return;
    }
    window.location.href = buildPlayStoreUrl();
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-10 dark:bg-dark-canvas">
      <section className={`w-full max-w-md overflow-hidden ${tw.card}`}>
        {post?.mediaUrl ? (
          <img
            src={post.mediaUrl}
            alt=""
            className="aspect-square w-full object-cover"
          />
        ) : null}
        <div className="space-y-6 p-6 sm:p-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary dark:text-primary-dark">
              Daywia
            </p>
            <h1 className={`mt-3 text-2xl tracking-tight ${tw.heading}`}>
              {profile?.fullName || copy.title}
            </h1>
            {profile ? (
              <div className="mt-4 flex items-center gap-3">
                {profile.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : null}
                <p className={tw.muted}>@{profile.username}</p>
              </div>
            ) : null}
            {post ? (
              <div className="mt-4 space-y-1">
                <p className={`font-medium ${tw.heading}`}>{post.authorName}</p>
                <p className={`text-sm ${tw.muted}`}>@{post.authorUsername}</p>
                {post.caption ? <p className={`pt-2 ${tw.heading}`}>{post.caption}</p> : null}
              </div>
            ) : null}
            <p className={`mt-4 text-sm leading-relaxed ${tw.muted}`}>
              {copy.description}
            </p>
          </div>

          <div className="space-y-3">
            <Button fullWidth onClick={openApp}>
              {isAndroidDevice() ? "Open in Daywia" : "Get Daywia for Android"}
            </Button>
            <a
              href={buildPlayStoreUrl()}
              className="block text-center text-sm text-primary dark:text-primary-dark"
            >
              Install from Google Play
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
